import React from 'react';
import { createUseStyles } from 'react-jss';
import ContactSection from './Contact';
import PageHeader from '../Shared/PageHeader'
import { Helmet } from 'react-helmet-async';
import Schedules from './Schedules';

function ContactUsPage() {
    const styles = useStyles();
    
    // Enhanced structured data 
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Integrated Holistic Psychotherapy",
        "description": "Reach out to Integrated Holistic Psychotherapy for counseling and psychotherapy services in Ruiru, Nairobi.",
        "url": "https://inhop.or.ke/contact",
        "mainEntity": {
            "@type": "MedicalBusiness",
            "name": "Integrated Holistic Psychotherapy",
            "url": "https://inhop.or.ke/",
            "logo": "https://inhop.or.ke/logo.webp",
            "telephone": "+254729569381",
            "email": "ihpsy18@gmail.com",
            "description": "Professional counseling and psychotherapy services for individuals, couples, families, and organizations.",
            "founder": {
                "@type": "Person",
                "name": "Dr. Ann Catherine Njeri Rukwaro"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Doctors Plaza, Ground Floor, Room F06",
                "addressLocality": "Ruiru",
                "addressRegion": "Nairobi",
                "postalCode": "", // Add if you have it
                "addressCountry": "KE"
            },
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": "-1.146683",
                "longitude": "36.95794"
            },
            "openingHoursSpecification": [
                {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": [
                        "Monday",
                        "Tuesday", 
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday"
                    ],
                    "opens": "08:00",
                    "closes": "18:00"
                },
                {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": "Sunday",
                    "opens": "00:00",
                    "closes": "00:00",
                    "validFrom": "2024-01-01",
                    "validThrough": "2025-12-31"
                }
            ],
            "contactPoint": [
                {
                    "@type": "ContactPoint",
                    "telephone": "+254729569381",
                    "contactType": "Customer Service",
                    "areaServed": "KE",
                    "availableLanguage": ["English", "Swahili"],
                    "hoursAvailable": {
                        "@type": "OpeningHoursSpecification",
                        "dayOfWeek": [
                            "Monday",
                            "Tuesday", 
                            "Wednesday",
                            "Thursday",
                            "Friday",
                            "Saturday"
                        ],
                        "opens": "08:00",
                        "closes": "18:00"
                    }
                },
                {
                    "@type": "ContactPoint",
                    "email": "ihpsy18@gmail.com",
                    "contactType": "Customer Support",
                    "areaServed": "KE",
                    "availableLanguage": ["English", "Swahili"]
                }
            ],
            "medicalSpecialty": [
                "Psychotherapy",
                "Counseling Psychology",
                "Mental Health Counseling"
            ],
            "serviceType": [
                "Individual Therapy",
                "Family Therapy", 
                "Couples Counseling",
                "Addiction Counseling",
                "Trauma Counseling",
                "Anxiety Treatment",
                "Depression Treatment",
                "Stress Management"
            ],
            "areaServed": {
                "@type": "Place",
                "name": "Nairobi County, Kenya"
            },
            "sameAs": [
                "https://www.facebook.com/inhop20/",
                "https://www.instagram.com/inhop20/",
                "https://x.com/inhop20"
            ]
        }
    };

    return (
        <>
            <Helmet>
                {/* Enhanced title with location */}
                <title>Contact Us | Integrated Holistic Psychotherapy</title>
                <meta
                    name="description"
                    content="Get in touch with us in Ruiru, Nairobi. Call +254729569381, email ihpsy18@gmail.com, or visit us at Doctors Plaza, Room F06. Professional counseling services."
                />
                <meta
                    name="keywords"
                    content="contact psychotherapy Ruiru, counseling Nairobi contact, Integrated Holistic Psychotherapy phone, mental health support Kenya, therapy inquiries Ruiru, counseling appointment booking"
                />
                <link rel="canonical" href="https://inhop.or.ke/contact" />

                {/* Enhanced Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://inhop.or.ke/contact" />
                <meta property="og:title" content="Contact Us | Integrated Holistic Psychotherapy | Ruiru, Nairobi" />
                <meta
                    property="og:description"
                    content="Reach Integrated Holistic Psychotherapy for professional counseling and mental health services in Ruiru, Nairobi. Multiple contact options available."
                />
                <meta property="og:image" content="https://inhop.or.ke/logo.webp" />
                <meta property="og:site_name" content="Integrated Holistic Psychotherapy" />
                <meta property="og:locale" content="en_KE" />

                {/* Enhanced Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@inhop20" />
                <meta name="twitter:creator" content="@inhop20" />
                <meta name="twitter:url" content="https://inhop.or.ke/contact" />
                <meta name="twitter:title" content="Contact Us | Integrated Holistic Psychotherapy | Ruiru, Nairobi" />
                <meta
                    name="twitter:description"
                    content="Contact Integrated Holistic Psychotherapy in Ruiru, Nairobi. Professional counseling and psychotherapy services. Call, email, or visit us."
                />
                <meta name="twitter:image" content="https://inhop.or.ke/logo.webp" />

                {/* Additional SEO meta tags */}
                <meta name="author" content="Integrated Holistic Psychotherapy" />
                <meta name="robots" content="index, follow" />
                <meta name="geo.region" content="KE-30" />
                <meta name="geo.placename" content="Ruiru, Nairobi, Kenya" />
                <meta name="geo.position" content="-1.146683;36.95794" />
                <meta name="ICBM" content="-1.146683, 36.95794" />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <div className={styles.pageContainer}>
                <div className={styles.contactImageHeader}>
                    <PageHeader pageTitle="Contact Us" />
                </div>
                <div>
                    <ContactSection />
                </div>
                <div>
                    <Schedules />
                </div>
            </div>
        </>
    )
}

const useStyles = createUseStyles({
    pageContainer: {
        position: "relative",
        height: "100%",
        width: "100%",
        fontFamily: "Open Sans, sans-serif",
        display: "flex",
        flexDirection: "column"
    },
    contactImageHeader: {
        // Add any specific styles for contact header if needed
    }
})

export default ContactUsPage;